import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/queryKeys';
import { fetchLastSeen, fetchProductUpdates, markSeen } from '@/services/productUpdates';

/**
 * Product updates + this user's unread count for the "What's new" bell.
 * Unread = updates published after the user's last-seen time (all if never seen).
 */
export function useProductUpdates() {
    const { user } = useAuth();
    const userId = user?.id ?? null;
    const queryClient = useQueryClient();

    const updatesQuery = useQuery({
        queryKey: queryKeys.productUpdates.list,
        queryFn: fetchProductUpdates,
        staleTime: 10 * 60 * 1000,
    });

    const lastSeenQuery = useQuery({
        queryKey: queryKeys.productUpdates.lastSeen(userId),
        queryFn: () => fetchLastSeen(userId ?? ''),
        enabled: !!userId,
        staleTime: 10 * 60 * 1000,
    });

    const markSeenMutation = useMutation({
        mutationFn: () => markSeen(userId ?? ''),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.productUpdates.lastSeen(userId) });
        },
    });

    const updates = updatesQuery.data ?? [];
    const lastSeen = lastSeenQuery.data ?? null;
    const unreadCount = lastSeen
        ? updates.filter((u) => u.published_at > lastSeen).length
        : updates.length;

    return {
        updates,
        unreadCount,
        isLoading: updatesQuery.isLoading,
        markSeen: () => {
            if (userId && unreadCount > 0) markSeenMutation.mutate();
        },
    };
}
